/* eslint-disable @typescript-eslint/no-explicit-any */
// Imports for external libraries go here.
import React, { FC, useState, useEffect, useRef } from 'react';

// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.
import { SearchFormPrimaryBlockProps } from './NGSearchForm.types';
import {
  NGRoomsAndGuests as RoomsAndGuests,
  NGSpecialRatesAndPoints as SpecialRates,
  NGUsePoints,
} from '../../molecules';
import { corporateCodeFormatLengthInvalidError } from '../../constants/lib/constants';
import { eventUtil } from '../../modules/utils';
import clsx from 'clsx';
import { useStaticDataContext } from '../../modules/context'; // AEM Labels
import { returnApplicableAlert } from '../../utils/src/utils';
import { SearchFormActiveState } from '../../constants';
import { useConditionalPageContextSERP } from '../../hooks';
import { useSearchFormStore, SearchFormState } from '../../modules/store/searchFormStore';

/**
 * search form below section consist of all the
 * below molecules like rates/point and room and guest
 * @returns
 */

export const SearchSecondaryBlock: FC<SearchFormPrimaryBlockProps> = ({
  model,
  isDesktopView,
  isAvailabilityScenario,
  isNextGenHP,
  isInModal,
  currentFormState,
  setCurrentFormState,
  setMobileFormModalState,
  onInputFieldsChangeHandle,
  pointsToolTipCloseSignal,
  isSearchFormActiveState,
  renderSecondaryBlock,
}) => {
  const {
    hideDestination,
    alertsListArray,
    disableSpecialRates,
    disableUsePointCertificates: disableUsePointCertificatesContext,
  } = useStaticDataContext();
  const [showSecondaryForm, setShowSecondaryForm] = useState(hideDestination ? true : false);
  const { isBulgariDomain } = useConditionalPageContextSERP();
  const formDestination = useSearchFormStore((state: SearchFormState) => state.destination);

  // Get Points configuration from AEM model and context
  const showPointsAsCheckbox = model?.showPointsAsCheckbox;
  // Use model override if available, otherwise fall back to context
  const disableUsePointCertificates = model?.disableUsePointCertificates ?? disableUsePointCertificatesContext;

  // Show standalone checkbox only if points are enabled AND showPointsAsCheckbox is true
  const showStandalonePointsCheckbox = !disableUsePointCertificates && showPointsAsCheckbox;

  useEffect(() => {
    eventUtil.on('showSecondaryForm', value => {
      setShowSecondaryForm(value);
    });
  }, [eventUtil]);

  // For NextGenHP desktop: show secondary form when destination is populated from session (repeat visitors)
  // Only trigger on initial session load (empty → non-empty transition), not during active typing
  const sessionShownRef = useRef(false);
  useEffect(() => {
    if (isNextGenHP && isDesktopView && formDestination?.displayText && !sessionShownRef.current) {
      sessionShownRef.current = true;
      setShowSecondaryForm(true);
    }
  }, [isNextGenHP, isDesktopView, formDestination?.displayText]);

  return (
    /**
     * search secondry block or bottom block of search form
     */
    <div
      className={clsx(
        'document_search_form',
        'standard',
        // NextGen standard mobile inactive: no padding/margin
        // NextGen standard mobile active (in modal), desktop, or HP: original padding/margin
        !isNextGenHP && !isDesktopView && !isInModal
          ? '' // No padding/margin for inactive mobile NextGen standard
          : 'pb-2 pb-lg-0 mr-lg-0 ml-lg-0 mb-3 mb-lg-0',
        !isNextGenHP && !isDesktopView && !isInModal && !renderSecondaryBlock ? 'col-2' : '',
        'flex-wrap flex-lg-nowrap',
        // NextGen: No padding (gap handled by parent flex container)
        // NextGenHP: No padding (gap handled by parent flex container)
        isNextGenHP ? 'py-0 px-0' : 'py-0 px-0',
        'search-form-secondary-wrapper',
        'hide-border-bottom',
        // NextGen (standard): Always visible (d-block on mobile, d-lg-flex on desktop)
        // NextGenHP: Hidden on desktop until showSecondaryForm is true, but visible in modal
        !showSecondaryForm && isNextGenHP && !isInModal
          ? 'd-none d-lg-none' // Hidden on all viewports for NextGenHP until form is active
          : 'd-block d-lg-flex', // Visible: block on mobile, flex on desktop
        disableSpecialRates ? 'col-lg-4 col-md-12' : ''
      )}
    >
      <div className={clsx('standard', 'mb-lg-0', 'custom_click_track')}>
        <RoomsAndGuests
          isDesktopView={isDesktopView}
          isAvailabilityScenario={isAvailabilityScenario}
          isNextGenHp={isNextGenHP}
          isInModal={isInModal}
          onInteraction={onInputFieldsChangeHandle}
          onCancel={() => {
            // If we're inside the FORM_STATE modal, return to it instead of closing
            if (isInModal) {
              setCurrentFormState?.(SearchFormActiveState.FORM_STATE);
              setMobileFormModalState?.(true);
            }
            // Otherwise, RoomsAndGuests manages its own state (no action needed)
          }}
          onMobileCompactViewClick={
            isAvailabilityScenario && !isDesktopView && !isInModal
              ? () => {
                  setCurrentFormState?.(SearchFormActiveState.FORM_STATE);
                  setMobileFormModalState?.(true);
                }
              : undefined
          }
        />
      </div>
      {/* Hide special rates on mobile NextGen inactive when isAvailabilityScenario is true */}
      {!disableSpecialRates &&
      !(isAvailabilityScenario && !isNextGenHP && !isDesktopView && !isInModal) &&
      !isBulgariDomain ? (
        <>
          <div className={clsx('mb-lg-0', 'custom_click_track', 'standard')}>
            <SpecialRates
              showPointsAsCheckbox={showPointsAsCheckbox}
              isSecondaryBlockEnabled={showSecondaryForm}
              isNextGenHP={isNextGenHP}
              isDesktopView={isDesktopView}
              isAvailabilityScenario={isAvailabilityScenario}
              isInModal={isInModal}
              currentFormState={currentFormState}
              errorMessage={returnApplicableAlert(corporateCodeFormatLengthInvalidError, alertsListArray)}
              onInteraction={onInputFieldsChangeHandle}
              externalCloseSignal={pointsToolTipCloseSignal}
              onCancel={() => {
                // If we're inside the FORM_STATE modal, return to it instead of closing
                if (isInModal) {
                  setCurrentFormState?.(SearchFormActiveState.FORM_STATE);
                  setMobileFormModalState?.(true);
                }
                // Otherwise, SpecialRates manages its own state (no action needed)
              }}
            />
          </div>
          {/* NextGen Standard: Only show checkbox when form is active (prevents loading flash)
              NextGenHP & Modal: Always show when points are enabled
              Mobile: Show when active for Standard, always for HP */}
          {showStandalonePointsCheckbox &&
            (isNextGenHP || !isDesktopView || isInModal) &&
            (isNextGenHP || isInModal || isSearchFormActiveState) && (
              <div className={clsx('mb-lg-0', 'custom_click_track', 'standard', 'pt-2', 'pt-lg-0')}>
                <NGUsePoints />
              </div>
            )}
        </>
      ) : (
        ' '
      )}
    </div>
  );
};
